"use client";

import { useEffect, useState, useCallback } from "react";
import { isTauri, checkForUpdate, type DownloadProgress } from "@/lib/tauri-bridge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Download, RefreshCw, CheckCircle2, ArrowRight } from "lucide-react";

type UpdatePhase = "available" | "downloading" | "installing" | "error";

/**
 * Custom auto-updater for Tauri desktop.
 * Shows a professional modal dialog with download progress,
 * release notes, and version comparison.
 */
export function TauriUpdater() {
  const [open, setOpen] = useState(false);
  const [phase, setPhase] = useState<UpdatePhase>("available");
  const [version, setVersion] = useState("");
  const [currentVersion, setCurrentVersion] = useState("");
  const [releaseNotes, setReleaseNotes] = useState("");
  const [progress, setProgress] = useState<DownloadProgress | null>(null);
  const [errorMsg, setErrorMsg] = useState("");
  const [downloadFn, setDownloadFn] = useState<
    ((onProgress?: (p: DownloadProgress) => void) => Promise<void>) | null
  >(null);

  useEffect(() => {
    if (!isTauri()) return;

    // On ne propose JAMAIS de mise à jour hors production.
    // Deux cas concernés :
    //  • la build desktop « Synoplix Alpha », qui démarre sur alpha.synoplix.com ;
    //  • l'app de prod basculée sur alpha/dev via le sélecteur d'environnement.
    // Sans ce garde-fou, l'updater installerait un binaire de PRODUCTION
    // par-dessus la build de test : on perdrait l'environnement pointé au
    // milieu d'une session de test, sans comprendre pourquoi.
    if (window.location.hostname !== "synoplix.com") return;

    const timer = setTimeout(async () => {
      const result = await checkForUpdate();
      if (result) {
        setVersion(result.info.version);
        setCurrentVersion(result.info.currentVersion);
        setReleaseNotes(result.info.body || "");
        // Wrap in a function-returning-function to avoid React treating it as a state updater
        setDownloadFn(() => result.downloadAndInstall);
        setOpen(true);
      }
    }, 3000);

    return () => clearTimeout(timer);
  }, []);

  const handleUpdate = useCallback(async () => {
    if (!downloadFn) return;
    setPhase("downloading");
    setProgress(null);
    setErrorMsg("");

    try {
      await downloadFn((p) => {
        setProgress(p);
        // When download is complete, switch to installing phase
        if (p.contentLength && p.downloaded >= p.contentLength) {
          setPhase("installing");
        }
      });
      // downloadAndInstall calls relaunch — we won't reach here normally
    } catch (err) {
      console.error("[TauriUpdater] Update failed:", err);
      setPhase("error");
      setErrorMsg(
        err instanceof Error
          ? err.message
          : typeof err === "string"
            ? err
            : "Erreur inconnue"
      );
    }
  }, [downloadFn]);

  const handleRetry = useCallback(() => {
    setPhase("available");
    setProgress(null);
    setErrorMsg("");
  }, []);

  const progressPercent =
    progress?.contentLength && progress.contentLength > 0
      ? Math.min(100, Math.round((progress.downloaded / progress.contentLength) * 100))
      : null;

  const downloadedMB = progress ? (progress.downloaded / (1024 * 1024)).toFixed(1) : "0";
  const totalMB = progress?.contentLength
    ? (progress.contentLength / (1024 * 1024)).toFixed(1)
    : null;

  // Format release notes: strip markdown headers, clean up
  const formattedNotes = releaseNotes
    .replace(/^#{1,3}\s+/gm, "")
    .replace(/\*\*([^*]+)\*\*/g, "$1")
    .trim();

  const isUpdating = phase === "downloading" || phase === "installing";

  return (
    <Dialog open={open} onOpenChange={isUpdating ? undefined : setOpen}>
      <DialogContent
        showCloseButton={!isUpdating}
        className="sm:max-w-md"
        onPointerDownOutside={(e) => { if (isUpdating) e.preventDefault(); }}
        onEscapeKeyDown={(e) => { if (isUpdating) e.preventDefault(); }}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {phase === "error" ? (
              "Erreur de mise a jour"
            ) : phase === "installing" ? (
              <>
                <CheckCircle2 className="h-5 w-5 text-green-500" />
                Installation en cours...
              </>
            ) : (
              <>
                <Download className="h-5 w-5 text-blue-500" />
                Mise a jour disponible
              </>
            )}
          </DialogTitle>
          <DialogDescription>
            {phase === "error" ? (
              errorMsg
            ) : (
              <span className="flex items-center gap-1.5">
                <span className="font-mono text-xs bg-muted px-1.5 py-0.5 rounded">
                  v{currentVersion}
                </span>
                <ArrowRight className="h-3 w-3 text-muted-foreground" />
                <span className="font-mono text-xs bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 px-1.5 py-0.5 rounded font-medium">
                  v{version}
                </span>
              </span>
            )}
          </DialogDescription>
        </DialogHeader>

        {/* Release notes */}
        {formattedNotes && phase !== "error" && (
          <div className="max-h-48 overflow-y-auto rounded-md border bg-muted/50 p-3 text-sm text-muted-foreground whitespace-pre-line">
            {formattedNotes}
          </div>
        )}

        {/* Download progress bar */}
        {(phase === "downloading" || phase === "installing") && (
          <div className="space-y-2">
            <div className="h-2.5 w-full overflow-hidden rounded-full bg-muted">
              <div
                className="h-full rounded-full bg-blue-500 transition-all duration-300 ease-out"
                style={{
                  width: progressPercent !== null ? `${progressPercent}%` : "100%",
                  ...(progressPercent === null && {
                    animation: "indeterminate 1.5s ease-in-out infinite",
                  }),
                }}
              />
            </div>
            <p className="text-xs text-muted-foreground text-center">
              {phase === "installing" ? (
                "Installation et redemarrage..."
              ) : progressPercent !== null ? (
                `${downloadedMB} / ${totalMB} Mo — ${progressPercent}%`
              ) : (
                `${downloadedMB} Mo telecharges...`
              )}
            </p>
          </div>
        )}

        <DialogFooter>
          {phase === "available" && (
            <>
              <Button variant="outline" onClick={() => setOpen(false)}>
                Plus tard
              </Button>
              <Button onClick={handleUpdate}>
                <Download className="mr-2 h-4 w-4" />
                Mettre a jour
              </Button>
            </>
          )}
          {phase === "error" && (
            <>
              <Button variant="outline" onClick={() => setOpen(false)}>
                Fermer
              </Button>
              <Button onClick={handleRetry}>
                <RefreshCw className="mr-2 h-4 w-4" />
                Reessayer
              </Button>
            </>
          )}
          {isUpdating && (
            <Button disabled variant="outline">
              {phase === "installing" ? "Redemarrage..." : "Telechargement..."}
            </Button>
          )}
        </DialogFooter>

        {/* Indeterminate animation keyframes */}
        <style>{`
          @keyframes indeterminate {
            0% { width: 0%; margin-left: 0%; }
            50% { width: 60%; margin-left: 20%; }
            100% { width: 0%; margin-left: 100%; }
          }
        `}</style>
      </DialogContent>
    </Dialog>
  );
}
